import React, { useState, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import { useAlert } from "components/UI_components/Alert";

// Utility for merging tailwind classes
export const cn = (...classes) => classes.filter(Boolean).join(" ");

// --- Button ---
export const Button = ({ variant = "primary", size = "md", className = "", children, ...props }) => {
    const baseStyles = "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:pointer-events-none";

    const variants = {
        primary: "bg-sm-primary text-white hover:bg-sm-hover shadow-sm",
        secondary: "bg-sm-secondary text-white hover:bg-sm-secondary/90 shadow-sm",
        accent: "bg-sm-accent text-white hover:bg-sm-accent/90 shadow-sm",
        outline: "border border-sm-border bg-transparent text-primary-text hover:bg-gray-bg",
        ghost: "bg-transparent text-primary-text hover:bg-gray-bg",
        destructive: "bg-destructive text-white hover:bg-destructive/90 shadow-sm",
        link: "text-sm-primary underline-offset-4 hover:underline",
    };

    const sizes = {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-9 w-9",
    };

    return (
        <button className={cn(baseStyles, variants[variant], sizes[size], className)} {...props}>
            {children}
        </button>
    );
};

// --- Badge ---
export const Badge = ({ variant = "primary", className = "", children }) => {
    const baseStyles = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border transition-colors";
    const variants = {
        primary: "border-transparent bg-sm-primary/10 text-sm-primary",
        secondary: "border-transparent bg-sm-secondary/10 text-sm-secondary",
        accent: "border-transparent bg-sm-accent/10 text-sm-accent",
        outline: "border-sm-border text-text-gray",
        destructive: "border-transparent bg-destructive/10 text-destructive",
        success: "border-transparent bg-light-green text-dark-green",
    };

    return (
        <div className={cn(baseStyles, variants[variant], className)}>
            {children}
        </div>
    );
};

// --- Card ---
export const Card = ({ className = "", children }) => (
    <div className={cn("rounded-xl border border-sm-border bg-white shadow-sm overflow-hidden", className)}>
        {children}
    </div>
);

export const CardHeader = ({ className = "", children }) => (
    <div className={cn("px-6 py-4 border-b border-sm-border/50", className)}>{children}</div>
);

export const CardTitle = ({ className = "", children }) => (
    <h3 className={cn("text-lg font-semibold text-primary-text leading-none", className)}>{children}</h3>
);

export const CardDescription = ({ className = "", children }) => (
    <p className={cn("text-sm text-text-gray mt-1.5", className)}>{children}</p>
);

export const CardContent = ({ className = "", children }) => (
    <div className={cn("p-6", className)}>{children}</div>
);

export const CardFooter = ({ className = "", children }) => (
    <div className={cn("px-6 py-4 border-t border-sm-border/50 bg-gray-bg/30", className)}>{children}</div>
);

// --- Input ---
export const Input = ({ className = "", ...props }) => (
    <input
        className={cn(
            "flex h-10 w-full rounded-lg border border-sm-border bg-input-bg px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-text-gray focus:outline-none focus:ring-2 focus:ring-sm-primary/20 focus:border-sm-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all",
            className
        )}
        {...props}
    />
);

// --- Label ---
export const Label = ({ className = "", ...props }) => (
    <label
        className={cn(
            "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-primary-text",
            className
        )}
        {...props}
    />
);

// --- Textarea ---
export const Textarea = ({ className = "", ...props }) => (
    <textarea
        className={cn(
            "flex min-h-[80px] w-full rounded-lg border border-sm-border bg-input-bg px-3 py-2 text-sm placeholder:text-text-gray focus:outline-none focus:ring-2 focus:ring-sm-primary/20 focus:border-sm-primary disabled:cursor-not-allowed disabled:opacity-50 transition-all",
            className
        )}
        {...props}
    />
);

// --- Select ---
export const Select = ({ value, onValueChange, children, className = "" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLabel, setSelectedLabel] = useState("");

    useEffect(() => {
        // Find label for initial value
        React.Children.forEach(children, (child) => {
            if (child && child.type === SelectContent) {
                React.Children.forEach(child.props.children, (item) => {
                    if (item && item.props && item.props.value === value) {
                        setSelectedLabel(item.props.children);
                    }
                });
            }
        });
    }, [value, children]);

    return (
        <div className={cn("relative", className)}>
            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute bottom-0 left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-sm-border bg-white p-1 shadow-lg animate-in fade-in zoom-in-95 duration-100">
                        {React.Children.map(children, (child) => {
                            if (child && child.type === SelectContent) {
                                return React.Children.map(child.props.children, (item) => {
                                    if (!item || !item.props) return null;
                                    return (
                                        <button
                                            key={item.props.value}
                                            className={cn(
                                                "flex w-full cursor-default select-none items-center rounded-md px-2 py-1.5 text-sm outline-none hover:bg-sm-primary/10 hover:text-sm-primary",
                                                item.props.value === value && "bg-sm-primary/5 text-sm-primary font-medium"
                                            )}
                                            onClick={() => {
                                                onValueChange(item.props.value);
                                                setSelectedLabel(item.props.children);
                                                setIsOpen(false);
                                            }}
                                        >
                                            {item.props.children}
                                        </button>
                                    );
                                });
                            }
                            return null;
                        })}
                    </div>
                </>
            )}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="flex h-10 w-full items-center justify-between rounded-lg border border-sm-border bg-input-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sm-primary/20 focus:border-sm-primary transition-all"
            >
                <span className={value ? "text-primary-text" : "text-text-gray"}>{selectedLabel || "Select..."}</span>
                <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform", isOpen && "rotate-180")} />
            </button>

        </div>
    );
};

export const SelectTrigger = ({ children }) => children;
export const SelectValue = () => null;
export const SelectContent = ({ children }) => children;
export const SelectItem = ({ children }) => children;

// --- Progress ---
export const Progress = ({ value = 0, className = "" }) => (
    <div className={cn("relative h-2 w-full overflow-hidden rounded-full bg-gray-bg", className)}>
        <div
            className="h-full w-full flex-1 bg-sm-primary transition-all"
            style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
        />
    </div>
);

// --- Avatar ---
export const Avatar = ({ className = "", children }) => (
    <div className={cn("relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)}>
        {children}
    </div>
);

export const AvatarImage = ({ src, className = "" }) => (
    <img src={src} className={cn("aspect-square h-full w-full", className)} alt="avatar" />
);

export const AvatarFallback = ({ className = "", children }) => (
    <div className={cn("flex h-full w-full items-center justify-center rounded-full bg-gray-bg text-text-gray", className)}>
        {children}
    </div>
);

// --- AlertDialog ---
export const AlertDialog = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            {React.Children.map(children, (child) => {
                if (child && child.type === AlertDialogTrigger) {
                    return React.cloneElement(child, { onClick: () => setIsOpen(true) });
                }
                if (isOpen && child && child.type === AlertDialogContent) {
                    return (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
                            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsOpen(false)} />
                            <div className="relative z-50 w-full max-w-lg rounded-xl bg-white p-6 shadow-xl animate-in zoom-in-95 duration-200">
                                {React.Children.map(child.props.children, (dialogChild) => {
                                    if (dialogChild && dialogChild.type === AlertDialogFooter) {
                                        return React.cloneElement(dialogChild, { onClose: () => setIsOpen(false) });
                                    }
                                    return dialogChild;
                                })}
                            </div>
                        </div>
                    );
                }
                return null;
            })}
        </>
    );
};

export const AlertDialogTrigger = ({ asChild, children, onClick }) => {
    if (asChild) {
        return React.cloneElement(children, { onClick });
    }
    return <button onClick={onClick}>{children}</button>;
};

export const AlertDialogContent = ({ children }) => children;
export const AlertDialogHeader = ({ children }) => <div className="mb-4">{children}</div>;
export const AlertDialogTitle = ({ className = "", children }) => (
    <h2 className={cn("text-lg font-semibold text-primary-text", className)}>{children}</h2>
);
export const AlertDialogDescription = ({ className = "", children }) => (
    <p className={cn("text-sm text-text-gray mt-2", className)}>{children}</p>
);
export const AlertDialogFooter = ({ children, onClose }) => (
    <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
        {React.Children.map(children, (child) => {
            if (child && child.type === AlertDialogCancel) {
                return React.cloneElement(child, { onClick: onClose });
            }
            return child;
        })}
    </div>
);
export const AlertDialogAction = ({ className = "", children, onClick }) => (
    <Button variant="primary" className={className} onClick={onClick}>{children}</Button>
);
export const AlertDialogCancel = ({ className = "", children, onClick }) => (
    <Button variant="outline" className={cn("mt-2 sm:mt-0", className)} onClick={onClick}>{children}</Button>
);

// --- Switch ---
export const Switch = ({ checked, onCheckedChange, className = "" }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onCheckedChange(!checked)}
        className={cn(
            "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-sm-primary/20 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            checked ? "bg-sm-primary" : "bg-gray-bg",
            className
        )}
    >
        <span
            className={cn(
                "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform",
                checked ? "translate-x-4" : "translate-x-0"
            )}
        />
    </button>
);
// --- Skeleton ---
export const Skeleton = ({ className = "", ...props }) => (
    <div
        className={cn("animate-pulse rounded-md bg-gray-bg/50", className)}
        {...props}
    />
);
